import { Flip, PixelCrop, Size } from "./ImageEditor.interface";

/**
 * Converts an array of File objects to image URLs
 * @param files Array of File objects
 * @returns Array of image URLs (as strings)
 */
// Updated filesToImageUrls utility function
export const filesToImageUrls = (files: File[] | File | null | undefined): string[] => {
  if (!files) return [];

  // Handle both array and single file cases
  const filesArray = Array.isArray(files) ? files : [files];

  return filesArray
    .filter(file => file?.type?.startsWith('image/'))
    .map(file => URL.createObjectURL(file));
};
export const cleanupImageUrls = (urls: string[]) => {
  urls.forEach(url => URL.revokeObjectURL(url));
};

/**
 * Creates an HTMLImageElement from a URL
 * @param url The image URL
 * @returns Promise that resolves with the loaded image
 */
export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues
    image.src = url;
  });

/**
 * Converts degrees to radians
 * @param degreeValue Angle in degrees
 * @returns Angle in radians
 */
export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Calculates the new bounding area of a rotated rectangle
 * @param width Original width
 * @param height Original height
 * @param rotation Rotation in degrees
 * @returns Size object with new width and height
 */
export function rotateSize(width: number, height: number, rotation: number): Size {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Crops and rotates an image
 * @param imageSrc Image URL or data URL
 * @param pixelCrop Crop dimensions and position
 * @param rotation Rotation in degrees (0-360)
 * @param flip Flip configuration
 * @returns Promise that resolves with the cropped image URL
 */
export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  rotation: number = 0,
  flip: Flip = { horizontal: false, vertical: false }
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const rotRad = getRadianAngle(rotation);

  // Calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    throw new Error('Could not get cropped canvas context');
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Return as a blob URL
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file) => {
      if (!file) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(URL.createObjectURL(file));
    }, 'image/jpeg');
  });
}